/**
 * ManageSeatsDialog Component
 * Dialog for managing seat count and viewing billing adjustments
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  useTheme,
  Divider
} from '@mui/material';
import {
  CloseOutlined as CloseIcon,
  RemoveOutlined as RemoveIcon,
  AddOutlined as AddIcon,
  KeyboardArrowDownOutlined as ExpandIcon
} from '@mui/icons-material';

interface ManageSeatsDialogProps {
  open: boolean;
  onClose: () => void;
  currentSeats: number;
  pricePerSeat: number;
  onConfirm: (newSeats: number) => void;
}

export const ManageSeatsDialog: React.FC<ManageSeatsDialogProps> = ({
  open,
  onClose,
  currentSeats,
  pricePerSeat,
  onConfirm
}) => {
  const theme = useTheme();
  const [seats, setSeats] = useState(currentSeats);
  const [creditsExpanded, setCreditsExpanded] = useState(false);

  const handleIncrement = () => setSeats(prev => prev + 1);
  const handleDecrement = () => setSeats(prev => Math.max(1, prev - 1));

  const subtotal = seats * pricePerSeat;
  const tax = 0;
  const total = subtotal + tax;
  const adjustment = (seats - currentSeats) * pricePerSeat;

  const handleConfirm = () => {
    onConfirm(seats);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.6)'
            : '0 8px 32px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          px: 3,
          pt: 2.5,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '16px' }}>
          Manage seats
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ borderRadius: '6px', color: 'text.secondary', ml: 2 }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 2 }}>
        {/* Seats Section */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '14px' }}>
                Seats
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px' }}>
                {currentSeats} used
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                (1x${pricePerSeat.toFixed(2)})
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '14px' }}>
                ${pricePerSeat.toFixed(2)}
              </Typography>
              <IconButton
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                <ExpandIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Seat Counter */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              borderRadius: '6px',
              border: `1px solid ${theme.palette.divider}`,
              width: 'fit-content',
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'
            }}
          >
            <IconButton
              size="small"
              onClick={handleDecrement}
              disabled={seats <= 1}
              sx={{
                width: 28,
                height: 28,
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(0,0,0,0.04)'
                }
              }}
            >
              <RemoveIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography
              sx={{
                minWidth: 32,
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              {seats}
            </Typography>
            <IconButton
              size="small"
              onClick={handleIncrement}
              sx={{
                width: 28,
                height: 28,
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(0,0,0,0.04)'
                }
              }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Automation Credits Section */}
        <Box
          sx={{
            p: 1.5,
            borderRadius: '6px',
            border: `1px solid ${theme.palette.divider}`,
            mb: 2.5,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
            cursor: 'pointer'
          }}
          onClick={() => setCreditsExpanded(!creditsExpanded)}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '13px' }}>
                Automation credits
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
                10000 included on Pro + 0k/ month
              </Typography>
            </Box>
            <IconButton
              size="small"
              sx={{
                color: 'text.secondary',
                transform: creditsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            >
              <ExpandIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Current Plan */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '13px', mb: 1.5 }}>
          Current plan renewing July 9th, 2026
        </Typography>

        {/* Line Items */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary' }}>
              {seats} seat × Pro (at ${pricePerSeat.toFixed(2)} / year)
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500 }}>
              ${subtotal.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Totals */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontSize: '13px' }}>
              Subtotal
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500 }}>
              ${subtotal.toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontSize: '13px' }}>
              Tax
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500 }}>
              ${tax.toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '13px' }}>
              Total at renewal
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '13px' }}>
              ${total.toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '13px' }}>
              Adjustment due today
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '13px' }}>
              ${adjustment.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 500,
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          Cancel <Typography component="span" sx={{ ml: 1, fontSize: '11px', color: 'text.disabled' }}>ESC</Typography>
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={seats === currentSeats}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 600,
            borderRadius: '6px',
            px: 2.5,
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

